import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { authFetch } from '../../auth';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownComponents } from './AIAgentMarkdown';
import { FALLBACK_MODEL_CATALOG, MODEL_FAMILY_LABELS, MODEL_TIER_LABELS, POLLY_VOICES, EDGE_VOICES, normalizeCatalogModel, formatLlmTrace, formatMsgTime } from './AIAgentConstants';

// Email Settings Sub-Tab
const EmailSubTab = () => {
  const [emailStatus, setEmailStatus] = useState(null);
  const [passphrase, setPassphrase] = useState('');
  const [unlockPass, setUnlockPass] = useState('');
  const [acctForm, setAcctForm] = useState({ label: '', imapHost: '', imapPort: 993, email: '', password: '', smtpHost: '', smtpPort: 587 });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState('');
  const [providerNote, setProviderNote] = useState('');
  const [detectTimeout, setDetectTimeout] = useState(null);

  useEffect(() => { loadEmailStatus(); }, []);

  // Auto-detect provider when email changes
  const handleEmailChange = (email) => {
    setField('email', email);
    if (detectTimeout) clearTimeout(detectTimeout);
    if (email.includes('@') && email.split('@')[1].includes('.')) {
      setDetectTimeout(setTimeout(async () => {
        try {
          const res = await authFetch(`/api/aiassistant/email/detect/${encodeURIComponent(email)}`);
          const p = await res.json();
          if (p && p.imapHost) {
            setAcctForm(prev => ({
              ...prev,
              imapHost: p.imapHost,
              imapPort: p.imapPort || 993,
              smtpHost: p.smtpHost || '',
              smtpPort: p.smtpPort || 587,
              label: prev.label || p.name?.toLowerCase() || ''
            }));
            setProviderNote(p.note || '');
          } else {
            setProviderNote(p.note || '');
          }
        } catch (e) {}
      }, 300));
    }
  };

  const loadEmailStatus = async () => {
    try {
      const res = await authFetch('/api/aiassistant/email/status');
      if (res.ok) setEmailStatus(await res.json());
    } catch (e) { console.error('email status:', e); }
  };

  const handleSetPassphrase = async () => {
    if (passphrase.length < 8) { setError('Min 8 characters'); return; }
    setSaving(true); setError('');
    try {
      const res = await authFetch('/api/aiassistant/email/setup-passphrase', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passphrase })
      });
      const data = await res.json();
      if (data.success) { setPassphrase(''); loadEmailStatus(); }
      else setError(data.error);
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const [stayUnlocked, setStayUnlocked] = useState(false);
  const handleUnlock = async () => {
    if (!unlockPass) return;
    setUnlocking(true); setError('');
    try {
      const res = await authFetch('/api/aiassistant/email/unlock', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passphrase: unlockPass, stayUnlocked })
      });
      const data = await res.json();
      if (data.success) { setUnlockPass(''); loadEmailStatus(); }
      else setError(data.error);
    } catch (e) { setError(e.message); }
    setUnlocking(false);
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null); setError('');
    try {
      const res = await authFetch('/api/aiassistant/email/test', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imapHost: acctForm.imapHost, imapPort: acctForm.imapPort, email: acctForm.email, password: acctForm.password })
      });
      const data = await res.json();
      setTestResult(data);
    } catch (e) { setTestResult({ error: e.message }); }
    setTesting(false);
  };

  const handleAddAccount = async () => {
    setSaving(true); setError('');
    try {
      const res = await authFetch('/api/aiassistant/email/accounts', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(acctForm)
      });
      const data = await res.json();
      if (data.success) {
        setAcctForm({ label: '', imapHost: '', imapPort: 993, email: '', password: '', smtpHost: '', smtpPort: 587 });
        setTestResult(null);
        loadEmailStatus();
      } else setError(data.error);
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const handleRemoveAccount = async (id) => {
    if (!window.confirm('Remove this email account?')) return;
    await authFetch(`/api/aiassistant/email/accounts/${id}`, { method: 'DELETE' });
    loadEmailStatus();
  };

  const handleSync = async (accountId) => {
    setSyncing(true); setSyncResult(null);
    try {
      const res = await authFetch('/api/aiassistant/email/sync', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId })
      });
      setSyncResult(await res.json());
    } catch (e) { setSyncResult({ error: e.message }); }
    setSyncing(false);
  };

  const setField = (k, v) => setAcctForm(prev => ({ ...prev, [k]: v }));

  if (!emailStatus) return <div className="settings-panel"><p>Loading...</p></div>;

  const handleReset = async () => {
    if (!window.confirm('This will remove ALL email accounts and cached emails. You can then set a new passphrase and re-add accounts. Continue?')) return;
    setSaving(true);
    try {
      const res = await authFetch('/api/aiassistant/email/reset', { method: 'POST' });
      if ((await res.json()).success) loadEmailStatus();
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  // Step 1: Set passphrase if not done
  if (!emailStatus.passphraseSet) {
    return (
      <div className="settings-panel">
        <h3 style={{margin:'0 0 8px'}}>Email Encryption Setup</h3>
        <div style={{background:'#f5f6fa',borderRadius:8,padding:14,marginBottom:16,fontSize:12,color:'#5a6480',lineHeight:1.6}}>
          <strong>How it works:</strong> Your email credentials (IMAP password) are encrypted with a passphrase that only you know. NumFree never stores your passphrase — it's used to derive an encryption key that protects your credentials at rest.
          <br/><br/>
          <strong>If you forget your passphrase:</strong> You can reset and re-add your email accounts. Your mail server password doesn't change — you just re-enter it.
          <br/><br/>
          <strong>Unlock frequency:</strong> After entering your passphrase, it stays unlocked for 24 hours. You only need to re-enter it after a server restart or after 24h.
        </div>
        <div className="form-group">
          <label>Email Passphrase (min 8 chars)</label>
          <input type="password" value={passphrase} onChange={e => setPassphrase(e.target.value)} placeholder="Choose a strong passphrase" />
        </div>
        {error && <p style={{color:'#c62828',fontSize:13}}>{error}</p>}
        <button className="btn-save" onClick={handleSetPassphrase} disabled={saving}>{saving ? 'Setting up...' : 'Set Passphrase'}</button>
      </div>
    );
  }

  // Step 2: Unlock if locked
  if (!emailStatus.unlocked) {
    return (
      <div className="settings-panel">
        <h3 style={{margin:'0 0 8px'}}>Email Vault Locked</h3>
        <p style={{color:'#666',fontSize:13,margin:'0 0 16px'}}>
          Enter your passphrase to unlock email access.
          {emailStatus.persistentUnlockEnabled
            ? ' Persistent unlock is already enabled, so phone calls and restarts can unlock automatically.'
            : ' Without persistent unlock, phone calls cannot unlock email automatically after a restart.'}
        </p>
        <div className="form-group">
          <label>Email Passphrase</label>
          <input type="password" value={unlockPass} onChange={e => setUnlockPass(e.target.value)} placeholder="Your email passphrase"
            onKeyDown={e => e.key === 'Enter' && handleUnlock()} />
        </div>
        {error && <p style={{color:'#c62828',fontSize:13}}>{error}</p>}
        <label style={{display:'flex',alignItems:'center',gap:8,margin:'10px 0',fontSize:13,cursor:'pointer'}}>
          <input type="checkbox" checked={stayUnlocked} onChange={e => setStayUnlocked(e.target.checked)} style={{width:16,height:16,accentColor:'#1E203F'}} />
          Keep email unlocked for phone calls and restarts
        </label>
        <p style={{color:'#999',fontSize:11,margin:'0 0 12px 24px'}}>
          {stayUnlocked
            ? 'NumFree stores a server-wrapped unlock key so your email vault can auto-unlock on future phone calls and after restarts. Less secure, much more convenient.'
            : 'You will need to re-enter your passphrase every 24 hours or after a server restart, and phone calls will not auto-unlock email.'}
        </p>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <button className="btn-save" onClick={handleUnlock} disabled={unlocking}>{unlocking ? 'Unlocking...' : 'Unlock'}</button>
          <button onClick={handleReset} style={{background:'none',border:'none',color:'#888',fontSize:12,cursor:'pointer',textDecoration:'underline'}}>Forgot passphrase? Reset</button>
        </div>
      </div>
    );
  }

  // Step 3: Unlocked — show accounts + add form
  return (
    <div className="settings-panel">
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
        <h3 style={{margin:0}}>Email Accounts</h3>
        <span style={{fontSize:12,color:'#4caf50',fontWeight:600}}>Vault Unlocked</span>
      </div>

      <div style={{background:'#f5f6fa',borderRadius:8,padding:14,marginBottom:16,fontSize:12,color:'#5a6480',lineHeight:1.6}}>
        <strong>What "Vault Unlocked" means:</strong> NumFree can currently read your encrypted email credentials and sync your inbox.
        <br/><br/>
        <strong>Persistent unlock:</strong> {emailStatus.persistentUnlockEnabled
          ? 'Enabled. Owner phone calls and server restarts can auto-unlock email.'
          : 'Disabled. After a restart, phone calls cannot unlock email until you unlock it here again.'}
      </div>

      {emailStatus.accounts.length > 0 && (
        <div style={{marginBottom:20}}>
          {emailStatus.accounts.map((a, i) => (
            <div key={i} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',background:'#f9f9f9',borderRadius:8,marginBottom:8,border:'1px solid #e0e0e0'}}>
              <div>
                <div style={{fontWeight:600,fontSize:14}}>{a.label || 'default'}</div>
                <div style={{fontSize:12,color:'#888'}}>{a.email} — {a.imapHost}</div>
              </div>
              <div style={{display:'flex',gap:8}}>
                <button className="btn-save" style={{padding:'6px 14px',fontSize:12}} onClick={() => handleSync(a.id)} disabled={syncing}>{syncing ? 'Syncing...' : 'Sync'}</button>
                <button style={{background:'#e53935',color:'#fff',border:'none',padding:'6px 14px',borderRadius:6,fontSize:12,cursor:'pointer'}} onClick={() => handleRemoveAccount(a.id)}>Remove</button>
              </div>
            </div>
          ))}
          {syncResult && <p style={{fontSize:12,color:syncResult.error ? '#c62828' : '#2e7d32',margin:'4px 0'}}>{syncResult.error || `Synced ${syncResult.synced} emails`}</p>}
        </div>
      )}

      <h4 style={{margin:'0 0 12px',fontSize:14}}>Add Email Account</h4>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
        <div className="form-group"><label>Label</label><input value={acctForm.label} onChange={e => setField('label', e.target.value)} placeholder="work, personal..." /></div>
        <div className="form-group"><label>Email Address</label><input value={acctForm.email} onChange={e => handleEmailChange(e.target.value)} placeholder="you@example.com" /></div>
        <div className="form-group"><label>IMAP Server</label><input value={acctForm.imapHost} onChange={e => setField('imapHost', e.target.value)} placeholder="imap.gmail.com" /></div>
        <div className="form-group"><label>IMAP Port</label><input type="number" value={acctForm.imapPort} onChange={e => setField('imapPort', parseInt(e.target.value) || 993)} /></div>
        <div className="form-group"><label>Password / App Password</label><input type="password" value={acctForm.password} onChange={e => setField('password', e.target.value)} /></div>
        <div className="form-group"><label>SMTP Server (optional)</label><input value={acctForm.smtpHost} onChange={e => setField('smtpHost', e.target.value)} placeholder="smtp.gmail.com" /></div>
      </div>

      {providerNote && <p style={{fontSize:12,color:'#e65100',background:'#fff3e0',padding:'8px 12px',borderRadius:6,margin:'8px 0'}}>
        {providerNote}
      </p>}
      {error && <p style={{color:'#c62828',fontSize:13,margin:'8px 0'}}>{error}</p>}
      {testResult && <p style={{fontSize:12,color:testResult.error ? '#c62828' : '#2e7d32',margin:'8px 0'}}>{testResult.error || `Connected! Folders: ${testResult.folders?.join(', ')}`}</p>}

      <div style={{display:'flex',gap:10,marginTop:12}}>
        <button className="btn-save" style={{background:'#546e7a'}} onClick={handleTest} disabled={testing || !acctForm.imapHost}>{testing ? 'Testing...' : 'Test Connection'}</button>
        <button className="btn-save" onClick={handleAddAccount} disabled={saving || !acctForm.imapHost || !acctForm.email || !acctForm.password}>{saving ? 'Adding...' : 'Add Account'}</button>
      </div>

      {!emailStatus.persistentUnlockEnabled && (
        <p style={{fontSize:12,color:'#8a6d3b',background:'#fff7e6',padding:'10px 12px',borderRadius:6,margin:'14px 0 0'}}>
          To make email available automatically on owner phone calls, lock the vault again and unlock it once with <strong>Keep email unlocked for phone calls and restarts</strong> checked.
        </p>
      )}
    </div>
  );
};

// Voice Cloner — record and create a custom ElevenLabs voice
const CLONE_SCRIPT_OWNER = "Hi, I'm your assistant. How can I help you today? You have two meetings tomorrow and three unread emails. Would you like me to read them to you? I can also check your calendar, manage your contacts, or look up flight information.";
const CLONE_SCRIPT_GUEST = "Hi, I'm Nick's assistant. Nick is not available right now, but I'd be happy to help. I can take a message, schedule a meeting, or answer any questions you might have. Please leave your name, number, and the reason for your call, and Nick will get back to you shortly. Thank you for reaching out.";

const VoiceCloner = ({ apiKey, onCreated }) => {
  const [expanded, setExpanded] = useState(false);
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [voiceName, setVoiceName] = useState('');
  const [error, setError] = useState('');
  const mediaRecRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      const chunks = [];
      mr.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecRef.current = mr;
      mr.start();
      setRecording(true);
      setDuration(0);
      setAudioBlob(null);
      setAudioUrl(null);
      setError('');
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } catch (e) { setError('Microphone access denied'); }
  };

  const stopRecording = () => {
    if (mediaRecRef.current && mediaRecRef.current.state !== 'inactive') mediaRecRef.current.stop();
    clearInterval(timerRef.current);
    setRecording(false);
  };

  const uploadVoice = async () => {
    if (!audioBlob || !voiceName.trim()) { setError('Name your voice first'); return; }
    if (duration < 15) { setError('Recording too short — need at least 15 seconds'); return; }
    setUploading(true); setError('');
    try {
      const form = new FormData();
      form.append('name', voiceName.trim());
      form.append('description', 'NumFree custom agent voice');
      form.append('files', audioBlob, 'recording.webm');
      const res = await fetch('https://api.elevenlabs.io/v1/voices/add', {
        method: 'POST',
        headers: { 'xi-api-key': apiKey },
        body: form
      });
      const data = await res.json();
      if (data.voice_id) {
        onCreated(data);
        setExpanded(false);
        setAudioBlob(null);
        setAudioUrl(null);
        setVoiceName('');
      } else {
        setError(data.detail?.message || 'Failed to create voice');
      }
    } catch (e) { setError(e.message); }
    setUploading(false);
  };

  if (!expanded) {
    return (
      <button onClick={() => setExpanded(true)}
        style={{marginTop:10,width:'100%',padding:'10px',border:'2px dashed #d0d1e0',borderRadius:8,background:'none',cursor:'pointer',fontSize:12,color:'#5a6480',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
        Clone Your Voice
      </button>
    );
  }

  return (
    <div style={{marginTop:10,border:'1px solid #d0d1e0',borderRadius:8,padding:12,background:'#f9f9fb'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
        <h5 style={{margin:0,fontSize:13}}>Clone Your Voice</h5>
        <button onClick={() => setExpanded(false)} style={{background:'none',border:'none',cursor:'pointer',color:'#888',fontSize:16}}>×</button>
      </div>
      <p style={{fontSize:11,color:'#666',margin:'0 0 8px',lineHeight:1.5}}>
        Read the script below aloud. Record at least 30 seconds in a quiet room for best results. Your voice will be used for all agent responses.
      </p>
      <div style={{marginBottom:10}}>
        <div style={{fontSize:11,fontWeight:600,color:'#1E203F',marginBottom:4}}>Script 1 — Owner greeting:</div>
        <div style={{background:'#fff',border:'1px solid #e8e8e8',borderRadius:6,padding:8,fontSize:12,color:'#333',lineHeight:1.5,marginBottom:8,fontStyle:'italic'}}>
          "{CLONE_SCRIPT_OWNER}"
        </div>
        <div style={{fontSize:11,fontWeight:600,color:'#e65100',marginBottom:4}}>Script 2 — Guest greeting:</div>
        <div style={{background:'#fff',border:'1px solid #e8e8e8',borderRadius:6,padding:8,fontSize:12,color:'#333',lineHeight:1.5,fontStyle:'italic'}}>
          "{CLONE_SCRIPT_GUEST}"
        </div>
        <p style={{fontSize:10,color:'#888',marginTop:4}}>Read both scripts in one recording for best results.</p>
      </div>

      {!audioBlob ? (
        <button onClick={recording ? stopRecording : startRecording}
          style={{width:'100%',padding:12,border:'none',borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:600,
            background: recording ? '#c62828' : '#1E203F', color:'#fff', display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/></svg>
          {recording ? `Stop Recording (${duration}s)` : 'Start Recording'}
        </button>
      ) : (
        <div>
          <audio src={audioUrl} controls style={{width:'100%',height:36,marginBottom:8}} />
          <p style={{fontSize:11,color:'#888',margin:'0 0 8px'}}>Duration: {duration}s {duration < 15 && '(need 15s+)'} {duration >= 30 && '— great length!'}</p>
          <input type="text" value={voiceName} onChange={e => setVoiceName(e.target.value)}
            placeholder="Name this voice (e.g. Nick's Voice)"
            style={{width:'100%',padding:'7px 10px',border:'1px solid #ddd',borderRadius:6,fontSize:12,marginBottom:8,boxSizing:'border-box'}} />
          <div style={{display:'flex',gap:6}}>
            <button onClick={uploadVoice} disabled={uploading || !voiceName.trim()}
              style={{flex:1,padding:'8px',border:'none',borderRadius:6,background:'#1E203F',color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer'}}>
              {uploading ? 'Creating...' : 'Create Voice'}
            </button>
            <button onClick={() => { setAudioBlob(null); setAudioUrl(null); setDuration(0); }}
              style={{padding:'8px 14px',border:'1px solid #ddd',borderRadius:6,background:'#fff',fontSize:12,cursor:'pointer'}}>
              Re-record
            </button>
          </div>
        </div>
      )}
      {error && <p style={{color:'#c62828',fontSize:11,marginTop:6}}>{error}</p>}
    </div>
  );
};

// Voice Sub-Tab — ElevenLabs-style UI

const VoiceSubTab = ({ ttsEngine, setTtsEngine, voice, setVoice, language, setLanguage, config, saving, onSave }) => {
  const systemElKey = config?.config?.envKeys?.ELEVEN_LABS_TOKEN || '';
  const [elVoices, setElVoices] = useState([]);
  const [elVoiceId, setElVoiceId] = useState(config?.config?.elevenlabsVoiceId || '');
  const [elApiKey, setElApiKey] = useState(config?.config?.elevenlabsApiKey || '');
  const [elVerified, setElVerified] = useState(!!(config?.config?.elevenlabsApiKey || systemElKey));
  const [elVerifying, setElVerifying] = useState(false);
  const [elPlan, setElPlan] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [llmModel, setLlmModel] = useState(config?.config?.llmModel || 'anthropic/claude-haiku-4.5');
  const audioRef = useRef(null);
  const hasAnyElKey = !!(elApiKey || systemElKey);
  const activeElKey = elApiKey || systemElKey;
  const hasVerifiedElKey = !!(activeElKey && elVerified && !(elPlan && elPlan.error));

  useEffect(() => {
    // Seed with saved custom voice so it shows immediately before verify completes
    const savedId = config?.config?.elevenlabsVoiceId;
    fetch('/api/aiassistant/elevenlabs-voices').then(r => r.ok ? r.json() : []).then(voices => {
      if (savedId && !voices.find(v => v.id === savedId)) {
        voices.unshift({ id: savedId, name: 'Custom Voice', desc: 'Your cloned voice', custom: true });
      }
      setElVoices(voices);
    }).catch(() => {});
    // Auto-verify a saved personal or system key on mount.
    if (config?.config?.elevenlabsApiKey) verifyElKey(config.config.elevenlabsApiKey);
    else if (systemElKey) verifyElKey(systemElKey);
  }, []);

  useEffect(() => {
    setTtsEngine(hasVerifiedElKey ? 'elevenlabs' : 'edge');
  }, [hasVerifiedElKey, setTtsEngine]);

  const verifyElKey = async (key) => {
    if (!key || key.length < 10) return;
    setElVerifying(true); setElVerified(false); setElPlan(null);
    try {
      const res = await authFetch('/api/aiassistant/elevenlabs-verify', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey: key })
      });
      const data = await res.json();
      if (data.valid) {
        setElVerified(true);
        setElPlan(data);
        if (data.voices?.length) setElVoices(data.voices);
      } else {
        setElVerified(false); setElPlan({ error: data.error || 'Invalid key' });
      }
    } catch(e) { setElPlan({ error: e.message }); }
    setElVerifying(false);
  };

  const playPreview = async (voiceId, engine) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlaying(voiceId);
    try {
      let url;
      if (engine === 'elevenlabs') {
        if (!hasVerifiedElKey) { alert('Add and verify an ElevenLabs key first'); setPlaying(null); return; }
        url = `/api/aiassistant/voice-preview-el/${voiceId}?key=${encodeURIComponent(activeElKey)}&t=${Date.now()}`;
      } else {
        url = `/api/aiassistant/voice-preview/${voiceId}?engine=${engine}&t=${Date.now()}`;
      }
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const audio = new Audio(blobUrl);
      audioRef.current = audio;
      audio.play();
      audio.onended = () => { URL.revokeObjectURL(blobUrl); setPlaying(null); audioRef.current = null; };
    } catch(e) { setPlaying(null); }
  };

  const handleSave = () => {
    onSave({
      ttsEngine: ttsEngine || (hasVerifiedElKey ? 'elevenlabs' : 'edge'),
      elevenlabsApiKey: elApiKey,
      elevenlabsVoiceId: elVoiceId,
      llmModel
    });
  };

  const currentVoices = elVoices;
  const selectedVoiceId = elVoiceId;

  return (
    <div className="settings-panel">
      <div style={{display:'flex',gap:24}}>
        {/* Left column: voices */}
        <div style={{flex:'0 0 280px'}}>
          <h4 style={{margin:'0 0 4px',fontSize:14}}>Voices</h4>
          <label style={{display:'block',fontSize:11,color:'#8892b0',textTransform:'uppercase',fontWeight:700,letterSpacing:'.4px',margin:'10px 0 4px'}}>Voice engine</label>
          <select data-testid="tts-engine-select" value={ttsEngine} onChange={e => setTtsEngine(e.target.value)}
            style={{width:'100%',padding:'7px 10px',borderRadius:8,border:'1px solid #dfe3ee',fontSize:13,background:'#fff'}}>
            <option value="edge">Edge — free</option>
            <option value="mimo">MiMo — Xiaomi</option>
            <option value="elevenlabs">ElevenLabs</option>
          </select>
          <p style={{color:'#888',fontSize:12,margin:'0 0 12px'}}>Select and preview the ElevenLabs voice for your agent.</p>

          {hasVerifiedElKey && (
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,color:'#2e7d32',marginBottom:8,background:'#e8f5e9',border:'1px solid #c8e6c9',borderRadius:6,padding:'8px 10px'}}>
                {elApiKey ? 'Using your verified ElevenLabs key.' : 'Using verified ElevenLabs system key from Settings → Keys.'}
              </div>
            </div>
          )}

          {/* Voice list */}
          {!hasVerifiedElKey && (
            <div style={{background:'#f9f9fb',border:'1px solid #e8e8e8',borderRadius:8,padding:12,marginBottom:8}}>
              <p style={{fontSize:12,color:'#555',margin:0,lineHeight:1.5}}>
                Voice selection and cloning appear when an ElevenLabs key is available and verified. If not, NumFree uses its hidden fallback voice automatically.
              </p>
            </div>
          )}
          <div style={{display:hasVerifiedElKey ? 'flex' : 'none',flexDirection:'column',gap:4,maxHeight:240,overflowY:'auto'}}>
            {currentVoices.map(v => {
              const vid = v.id;
              const isSelected = vid === selectedVoiceId;
              return (
                <div key={vid} onClick={() => setElVoiceId(vid)}
                  style={{display:'flex',alignItems:'center',gap:8,padding:'8px 10px',border:'1px solid '+(isSelected?'#1E203F':'#e8e8e8'),
                    borderRadius:8,cursor:'pointer',background:isSelected?'#f0f1f8':'#fff',transition:'border-color 0.15s'}}>
                  {isSelected && <span style={{width:8,height:8,borderRadius:'50%',background:'#4caf50',flexShrink:0}} />}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:600,fontSize:13}}>{v.name}{v.custom && <span style={{fontSize:10,color:'#1E203F',marginLeft:4,background:'#e8e8f0',padding:'1px 6px',borderRadius:4}}>Custom</span>}</div>
                    <div style={{fontSize:11,color:'#888'}}>{v.desc}</div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); playPreview(vid, 'elevenlabs'); }}
                    style={{background:'none',border:'none',cursor:'pointer',padding:4,color:playing===vid?'#1E203F':'#999'}}>
                    {playing === vid ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Voice Cloning — ElevenLabs only */}
          {hasVerifiedElKey && <VoiceCloner apiKey={activeElKey} onCreated={(v) => { setElVoices(prev => [{ id: v.voice_id, name: v.name, desc: 'Custom cloned voice', custom: true }, ...prev]); setElVoiceId(v.voice_id); }} />}

          {!hasVerifiedElKey && (
            <div style={{marginTop:12}}>
              <div style={{display:'flex',gap:6}}>
                <input
                  type="password"
                  value={elApiKey}
                  onChange={e => { setElApiKey(e.target.value); setElVerified(false); setElPlan(null); }}
                  placeholder={systemElKey ? 'Enter a working ElevenLabs API key' : 'ElevenLabs API key'}
                  style={{flex:1,padding:'7px 10px',border:'1px solid '+(elPlan?.error?'#e53935':'#ddd'),borderRadius:6,fontSize:12,boxSizing:'border-box'}}
                />
                <button
                  onClick={() => verifyElKey(elApiKey)}
                  disabled={elVerifying || !elApiKey}
                  style={{padding:'7px 14px',border:'1px solid #ddd',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer',background:'#fff',color:'#666'}}
                >
                  {elVerifying ? '...' : 'Verify'}
                </button>
              </div>
              {elPlan && !elPlan.error && (
                <div style={{fontSize:11,color:'#2e7d32',marginTop:4}}>
                  {elVoices.length || elPlan.voiceCount || 0} voices available
                  {elPlan.plan && elPlan.plan !== 'unknown' ? ` — ${elPlan.plan} plan` : ''}
                  {elPlan.characterLimit > 0 ? ` — ${((elPlan.charactersUsed/elPlan.characterLimit)*100).toFixed(0)}% used (${elPlan.charactersUsed?.toLocaleString()}/${elPlan.characterLimit?.toLocaleString()} chars)` : ''}
                </div>
              )}
              {elPlan?.error && <div style={{fontSize:11,color:'#e53935',marginTop:4}}>Invalid key: {elPlan.error}</div>}
              <div style={{fontSize:10,color:'#999',marginTop:4}}>
                {systemElKey ? 'The saved system token is not currently verified. Add a working key here or update `ELEVEN_LABS_TOKEN` in Keys.' : 'Add your key here or save `ELEVEN_LABS_TOKEN` in Keys.'}
              </div>
            </div>
          )}
        </div>

        {/* Right column: language + LLM */}
        <div style={{flex:1}}>
          <h4 style={{margin:'0 0 4px',fontSize:14}}>Language</h4>
          <p style={{color:'#888',fontSize:12,margin:'0 0 8px'}}>Choose the default language.</p>
          <select value={language} onChange={e => setLanguage(e.target.value)}
            style={{width:'100%',padding:'8px 10px',border:'1px solid #ddd',borderRadius:8,fontSize:13,marginBottom:20}}>
            <option value="en-US">English (US)</option>
            <option value="en-GB">English (UK)</option>
            <option value="es-ES">Spanish</option>
            <option value="fr-FR">French</option>
            <option value="de-DE">German</option>
            <option value="pt-BR">Portuguese (Brazil)</option>
            <option value="ja-JP">Japanese</option>
            <option value="zh-CN">Chinese (Mandarin)</option>
            <option value="ko-KR">Korean</option>
            <option value="ar-SA">Arabic</option>
          </select>

          <h4 style={{margin:'0 0 4px',fontSize:14}}>Dictation</h4>
          <div style={{background:'#f9f9fb',border:'1px solid #e8e8e8',borderRadius:8,padding:12,marginBottom:20}}>
            <p style={{color:'#666',fontSize:12,margin:'0 0 10px'}}>How the agent handles information that's hard to capture by voice (emails, addresses, codes).</p>
            <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer'}}>
              <input type="checkbox" defaultChecked={config?.config?.dictation?.askToType !== false}
                onChange={e => authFetch('/api/aiassistant/config', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dictation: { askToType: e.target.checked } }) })}
                style={{width:16,height:16,accentColor:'#1E203F'}} />
              Ask to type confirmation when available
            </label>
            <p style={{color:'#999',fontSize:11,margin:'6px 0 0 24px',lineHeight:1.5}}>Voice recognition often garbles email addresses, phone numbers, and spelling. When enabled, the agent detects this and asks the caller to type the information via text message instead — ensuring accuracy for critical data like booking confirmations, contact details, and account information.</p>
          </div>

          {/* LLM model picker moved to Agent tab */}

        </div>
      </div>

      <button className="btn-save" onClick={() => handleSave()} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
    </div>
  );
};

// Access Log Sub-Tab
// Environment Keys Sub-Tab — user-supplied API keys for tools
const KNOWN_KEYS = [
  { key: 'GOOGLE_AI_API_KEY', label: 'Google AI (Gemini)', desc: 'Required for Gemini Live voice (ai.google.dev)', free: true },
  { key: 'OPENAI_API_KEY', label: 'OpenAI', desc: 'Direct OpenAI route for GPT-4o models (platform.openai.com)', free: false },
  { key: 'DEEPSEEK_API_KEY', label: 'DeepSeek', desc: 'Direct DeepSeek API for V/R models (platform.deepseek.com)', free: false },
  { key: 'CLOUDFLARE_API_TOKEN', label: 'Cloudflare Workers AI', desc: 'Token for Cloudflare-hosted models (Llama, Qwen, DeepSeek-R1)', free: true },
  { key: 'CLOUDFLARE_ACCOUNT_ID', label: 'Cloudflare Account ID', desc: 'Account ID paired with Cloudflare token', free: true },
  { key: 'MINIMAX_API_KEY', label: 'MiniMax', desc: 'Direct MiniMax route (minimax.io)', free: false },
  { key: 'OPENROUTER_API_KEY', label: 'OpenRouter (fallback)', desc: 'OpenRouter master key — fallback when direct providers fail', free: false },
  { key: 'ELEVEN_LABS_TOKEN', label: 'ElevenLabs', desc: 'System ElevenLabs token for voice selection and cloning', free: false },
  { key: 'AVIATIONSTACK_API_KEY', label: 'AviationStack', desc: 'Flight tracking (aviationstack.com)', free: true },
  { key: 'WEATHERAPI_KEY', label: 'WeatherAPI', desc: 'Weather data (weatherapi.com)', free: true },
  { key: 'GOOGLE_MAPS_API_KEY', label: 'Google Maps', desc: 'Places & directions', free: false },
];

const EnvKeysSubTab = ({ config, onSave, saving }) => {
  const existing = config?.config?.envKeys || {};
  const [keys, setKeys] = useState(existing);
  const [customKey, setCustomKey] = useState('');
  const [customVal, setCustomVal] = useState('');

  const updateKey = (k, v) => setKeys(prev => ({ ...prev, [k]: v }));
  const removeKey = (k) => setKeys(prev => { const n = { ...prev }; delete n[k]; return n; });
  const addCustom = () => {
    if (!customKey.trim()) return;
    const k = customKey.trim().toUpperCase().replace(/[^A-Z0-9_]/g, '_');
    setKeys(prev => ({ ...prev, [k]: customVal }));
    setCustomKey(''); setCustomVal('');
  };

  const handleSave = () => onSave({ envKeys: keys });

  return (
    <div className="settings-panel">
      <h4 style={{margin:'0 0 4px',fontSize:14}}>API Keys</h4>
      <p style={{color:'#888',fontSize:12,margin:'0 0 16px'}}>Add your own API keys to unlock additional tools. Keys are stored securely and used only for your agent.</p>

      {KNOWN_KEYS.map(kd => (
        <div key={kd.key} style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
          <div style={{flex:'0 0 140px'}}>
            <div style={{fontWeight:600,fontSize:13}}>{kd.label}</div>
            <div style={{fontSize:11,color:'#888'}}>{kd.desc}</div>
          </div>
          <input type="password" value={keys[kd.key] || ''} onChange={e => updateKey(kd.key, e.target.value)}
            placeholder={`${kd.key}`}
            style={{flex:1,padding:'7px 10px',border:'1px solid '+(keys[kd.key]?'#4caf50':'#ddd'),borderRadius:6,fontSize:12,boxSizing:'border-box'}} />
          {keys[kd.key] && <button onClick={() => removeKey(kd.key)} style={{background:'none',border:'none',color:'#c62828',cursor:'pointer',fontSize:16}}>×</button>}
        </div>
      ))}

      {/* Custom keys */}
      {Object.keys(keys).filter(k => !KNOWN_KEYS.find(kd => kd.key === k)).map(k => (
        <div key={k} style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
          <div style={{flex:'0 0 140px',fontWeight:600,fontSize:13,fontFamily:'monospace'}}>{k}</div>
          <input type="password" value={keys[k] || ''} onChange={e => updateKey(k, e.target.value)}
            style={{flex:1,padding:'7px 10px',border:'1px solid #4caf50',borderRadius:6,fontSize:12,boxSizing:'border-box'}} />
          <button onClick={() => removeKey(k)} style={{background:'none',border:'none',color:'#c62828',cursor:'pointer',fontSize:16}}>×</button>
        </div>
      ))}

      <div style={{display:'flex',gap:6,marginTop:12,marginBottom:16}}>
        <input type="text" value={customKey} onChange={e => setCustomKey(e.target.value)} placeholder="KEY_NAME"
          style={{width:140,padding:'6px 8px',border:'1px solid #ddd',borderRadius:6,fontSize:12,fontFamily:'monospace'}} />
        <input type="text" value={customVal} onChange={e => setCustomVal(e.target.value)} placeholder="value"
          style={{flex:1,padding:'6px 8px',border:'1px solid #ddd',borderRadius:6,fontSize:12}} />
        <button onClick={addCustom} disabled={!customKey.trim()}
          style={{padding:'6px 14px',border:'1px solid #ddd',borderRadius:6,fontSize:12,cursor:'pointer',background:'#f5f5f5'}}>Add</button>
      </div>

      <button className="btn-save" onClick={() => handleSave()} disabled={saving}>{saving ? 'Saving...' : 'Save Keys'}</button>
    </div>
  );
};

// Tuning Log Sub-Tab — shows refusals with explanations + one-click fix
const TYPE_LABELS = { permission_denied: 'Permission Denied', guest_blocked: 'Guest Blocked', refusal: 'Agent Refused' };
const TYPE_COLORS = { permission_denied: '#e65100', guest_blocked: '#6a1b9a', refusal: '#c62828' };

const TuningSubTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allowing, setAllowing] = useState(null);

  useEffect(() => { loadLogs(); }, []);
  const loadLogs = async () => {
    setLoading(true);
    try { const res = await authFetch('/api/aiassistant/tuning-log'); if (res.ok) setLogs(await res.json()); } catch (e) {}
    setLoading(false);
  };

  const allowAction = async (log) => {
    if (!log.resource || !log.permission) return;
    const key = `${log.callerTier}.${log.resource}.${log.permission}`;
    setAllowing(key);
    try {
      const res = await authFetch('/api/aiassistant/tuning-log/allow', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resource: log.resource, permission: log.permission, tier: log.callerTier || 'owner' })
      });
      if ((await res.json()).success) {
        setLogs(prev => prev.map(l => (l.resource === log.resource && l.permission === log.permission && l.callerTier === log.callerTier) ? { ...l, resolved: true } : l));
      }
    } catch (e) {}
    setAllowing(null);
  };

  if (loading) return <div className="settings-panel"><p>Loading...</p></div>;

  // Group by type + resource + tier, show count
  const grouped = {};
  logs.filter(l => !l.resolved).forEach(l => {
    const key = `${l.type}:${l.resource || 'general'}:${l.callerTier || 'owner'}`;
    if (!grouped[key]) grouped[key] = { ...l, count: 0, messages: [] };
    grouped[key].count++;
    if (l.userMessage && grouped[key].messages.length < 3) grouped[key].messages.push(l.userMessage);
    if (new Date(l.timestamp) > new Date(grouped[key].timestamp)) grouped[key].timestamp = l.timestamp;
  });
  const entries = Object.values(grouped).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <div className="settings-panel">
      <h3 style={{margin:'0 0 4px'}}>Tuning</h3>
      <p style={{color:'#666',fontSize:13,margin:'0 0 16px'}}>When your agent refuses a request or lacks permission, it shows here with the reason and a fix.</p>

      {entries.length === 0 ? (
        <p style={{color:'#999',fontSize:13,textAlign:'center',padding:20}}>No issues detected yet. Interact with your agent and any refusals will appear here.</p>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {entries.map((e, i) => (
            <div key={i} style={{padding:'12px 14px',background:'#fff',border:'1px solid #e8e8e8',borderRadius:8,borderLeft:'4px solid '+(TYPE_COLORS[e.type]||'#888')}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6}}>
                <div>
                  <span style={{fontSize:10,fontWeight:700,textTransform:'uppercase',color:TYPE_COLORS[e.type]||'#888',letterSpacing:'0.5px'}}>{TYPE_LABELS[e.type]||e.type}</span>
                  {e.callerTier && <span style={{fontSize:10,marginLeft:6,color:'#888'}}>({e.callerTier})</span>}
                  <span style={{fontSize:11,color:'#aaa',marginLeft:8}}>{e.count}x — last: {new Date(e.timestamp).toLocaleString()}</span>
                </div>
                {e.resource && e.permission && !e.resolved && (
                  <button onClick={() => allowAction(e)} disabled={allowing === `${e.callerTier}.${e.resource}.${e.permission}`}
                    style={{padding:'4px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer',background:e.resolved?'#e8f5e9':'#fff',color:e.resolved?'#2e7d32':'#1E203F'}}>
                    {allowing === `${e.callerTier}.${e.resource}.${e.permission}` ? '...' : 'Allow'}
                  </button>
                )}
              </div>
              <div style={{fontSize:13,fontWeight:500,color:'#1E203F',marginBottom:4}}>
                {e.resource ? `${e.resource}.${e.permission || e.operation}` : 'General'}
              </div>
              <div style={{fontSize:12,color:'#5a6480',marginBottom:6}}>{e.reason}</div>
              {e.fix && <div style={{fontSize:11,color:'#1E203F',background:'#f5f6fa',padding:'6px 10px',borderRadius:4}}>{e.fix}</div>}
              {e.messages && e.messages.length > 0 && (
                <div style={{marginTop:6}}>
                  <div style={{fontSize:10,color:'#aaa',marginBottom:2}}>User said:</div>
                  {e.messages.map((m, mi) => (
                    <div key={mi} style={{fontSize:11,color:'#666',fontStyle:'italic'}}>"{m}"</div>
                  ))}
                </div>
              )}
              {e.agentReply && (
                <div style={{marginTop:4}}>
                  <div style={{fontSize:10,color:'#aaa'}}>Agent replied:</div>
                  <div style={{fontSize:11,color:'#888',fontStyle:'italic'}}>"{e.agentReply.substring(0, 150)}{e.agentReply.length > 150 ? '...' : ''}"</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AccessLogSubTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(null);

  useEffect(() => { loadLogs(); }, []);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/aiassistant/access-log');
      if (res.ok) setLogs(await res.json());
    } catch (e) {}
    setLoading(false);
  };

  const quickEnable = async (resource, permission, tier) => {
    const key = `${tier}.${resource}.${permission}`;
    setEnabling(key);
    try {
      const res = await authFetch('/api/aiassistant/access-log/enable', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resource, permission, tier })
      });
      const data = await res.json();
      if (data.success) {
        setLogs(prev => prev.map(l => (l.resource === resource && l.permission === permission && l.callerTier === tier) ? { ...l, _enabled: true } : l));
      }
    } catch (e) {}
    setEnabling(null);
  };

  if (loading) return <div className="settings-panel"><p>Loading...</p></div>;

  // Group by resource+permission+tier, show count + latest timestamp
  const grouped = {};
  logs.forEach(l => {
    const key = `${l.callerTier}:${l.resource}:${l.permission}`;
    if (!grouped[key]) grouped[key] = { ...l, count: 0 };
    grouped[key].count++;
    if (new Date(l.timestamp) > new Date(grouped[key].timestamp)) grouped[key].timestamp = l.timestamp;
  });
  const entries = Object.values(grouped).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <div className="settings-panel">
      <h3 style={{margin:'0 0 8px'}}>Access Denied Log</h3>
      <p style={{color:'#666',fontSize:13,margin:'0 0 16px'}}>Shows when the agent tried to use a tool but was blocked by permissions. Enable directly from here.</p>

      {entries.length === 0 ? (
        <p style={{color:'#999',fontSize:13,textAlign:'center',padding:20}}>No denied requests yet.</p>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {entries.map((e, i) => (
            <div key={i} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',background: e._enabled ? '#e8f5e9' : '#fff3f0',border:'1px solid ' + (e._enabled ? '#c8e6c9' : '#ffcdd2'),borderRadius:8}}>
              <div style={{flex:1}}>
                <div style={{fontWeight:600,fontSize:13}}>
                  <span style={{color:e.callerTier === 'guest' ? '#e65100' : '#1565c0',fontSize:11,textTransform:'uppercase',marginRight:6}}>{e.callerTier}</span>
                  {e.resource}.{e.permission}
                </div>
                <div style={{fontSize:11,color:'#888',marginTop:2}}>
                  {e.count} denied request{e.count > 1 ? 's' : ''} — last: {new Date(e.timestamp).toLocaleString()}
                </div>
              </div>
              {e._enabled ? (
                <span style={{fontSize:12,color:'#2e7d32',fontWeight:600}}>Enabled</span>
              ) : (
                <button className="btn-save" style={{padding:'5px 12px',fontSize:11}}
                  onClick={() => quickEnable(e.resource, e.permission, e.callerTier)}
                  disabled={enabling === `${e.callerTier}.${e.resource}.${e.permission}`}>
                  {enabling === `${e.callerTier}.${e.resource}.${e.permission}` ? '...' : 'Enable'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const OPENCLAW_COMMAND_SPECS = [
  { key: 'agent', desc: 'Run one agent turn', ui: 'Chat tab', voice: 'Phone/Web', support: 'working', priority: '', notes: 'Core feature, fully working', params: [{ key: 'prompt', label: 'Prompt', type: 'textarea', placeholder: 'Ask OpenClaw to do something...' }] },
  { key: 'channels', desc: 'Manage chat channels', ui: 'WhatsApp tab only', voice: 'No', support: 'partial', priority: 'high', notes: 'Discord + Telegram still missing', params: [{ key: 'action', label: 'Action', type: 'select', options: ['list', 'status', 'connect', 'disconnect'] }, { key: 'channel', label: 'Channel', type: 'select', options: ['whatsapp', 'discord', 'telegram'] }] },
  { key: 'config', desc: 'Get or set config', ui: 'Settings tabs', voice: 'No', support: 'working', priority: '', notes: 'Maps to Agent, Voice, Advanced, Keys', params: [{ key: 'action', label: 'Action', type: 'select', options: ['list', 'get', 'set'] }, { key: 'name', label: 'Setting', type: 'text', placeholder: 'greeting' }, { key: 'value', label: 'Value', type: 'text', placeholder: 'New value' }] },
  { key: 'configure', desc: 'Interactive setup', ui: 'Partial Settings', voice: 'No', support: 'partial', priority: 'med', notes: 'Wizard still missing' },
  { key: 'cron', desc: 'Scheduled jobs', ui: 'Missing', voice: 'No', support: 'missing', priority: 'high', notes: 'Needed for reminders and reports', params: [{ key: 'action', label: 'Action', type: 'select', options: ['list', 'add', 'remove'] }, { key: 'schedule', label: 'Schedule', type: 'text', placeholder: '0 9 * * 1' }, { key: 'task', label: 'Task', type: 'textarea', placeholder: 'Send weekly report' }] },
  { key: 'dashboard', desc: 'Open control UI', ui: 'Terminal tab', voice: 'No', support: 'working', priority: '', notes: 'Terminal tab is the current path' },
  { key: 'devices', desc: 'Device pairing', ui: 'Missing', voice: 'No', support: 'missing', priority: 'low', notes: 'Future mobile app pairing' },
  { key: 'directory', desc: 'Contact and group lookup', ui: 'Contacts page', voice: 'Via tools', support: 'working', priority: '', notes: 'Already exposed in assistant tools', params: [{ key: 'query', label: 'Query', type: 'text', placeholder: 'John or Acme' }] },
  { key: 'dns', desc: 'DNS helpers', ui: 'Missing', voice: 'No', support: 'missing', priority: 'low', notes: 'Infra-level command' },
  { key: 'doctor', desc: 'Health checks', ui: 'Status badge only', voice: 'No', support: 'partial', priority: 'med', notes: 'Should expose richer diagnostics' },
  { key: 'gateway', desc: 'WebSocket gateway', ui: 'Internal only', voice: 'No', support: 'internal', priority: 'low', notes: 'Infrastructure command' },
  { key: 'health', desc: 'Gateway health', ui: 'Online/Offline badge', voice: 'No', support: 'working', priority: '', notes: 'Lightweight health check' },
  { key: 'hooks', desc: 'Agent hooks', ui: 'Missing', voice: 'No', support: 'missing', priority: 'med', notes: 'Webhook-style event hooks', params: [{ key: 'action', label: 'Action', type: 'select', options: ['list', 'add', 'remove'] }, { key: 'event', label: 'Event', type: 'select', options: ['on_missed_call', 'on_booking', 'on_new_contact'] }, { key: 'target', label: 'Target URL', type: 'text', placeholder: 'https://example.com/hook' }] },
  { key: 'logs', desc: 'Tail gateway logs', ui: 'Access > Tune only', voice: 'No', support: 'partial', priority: 'high', notes: 'Need full logs with tool results', params: [{ key: 'tail', label: 'Tail lines', type: 'number', placeholder: '100' }] },
  { key: 'memory', desc: 'Search memory files', ui: 'Missing', voice: 'No', support: 'missing', priority: 'med', notes: 'Future knowledge base UI', params: [{ key: 'query', label: 'Query', type: 'text', placeholder: 'travel preferences' }] },
  { key: 'message', desc: 'Send or read messages', ui: 'SMS page', voice: 'Via tools', support: 'working', priority: '', notes: 'Maps to SMS/WhatsApp messaging', params: [{ key: 'action', label: 'Action', type: 'select', options: ['list', 'send'] }, { key: 'channel', label: 'Channel', type: 'select', options: ['sms', 'whatsapp'] }, { key: 'to', label: 'To', type: 'phone', placeholder: '+16505551212' }, { key: 'text', label: 'Message', type: 'textarea', placeholder: 'Hello from OpenClaw' }] },
  { key: 'models', desc: 'Discover and configure models', ui: 'Agent settings', voice: 'No', support: 'working', priority: '', notes: 'Model picker already exists', params: [{ key: 'action', label: 'Action', type: 'select', options: ['list', 'use'] }, { key: 'model', label: 'Model', type: 'text', placeholder: 'anthropic/claude-haiku-4.5' }] },
  { key: 'onboard', desc: 'Interactive onboarding', ui: 'Missing', voice: 'No', support: 'missing', priority: 'high', notes: 'Critical setup wizard gap' },
  { key: 'plugins', desc: 'Manage plugins', ui: 'Missing', voice: 'No', support: 'missing', priority: 'med', notes: 'Marketplace is prompts only today', params: [{ key: 'action', label: 'Action', type: 'select', options: ['list', 'install', 'remove'] }, { key: 'plugin', label: 'Plugin', type: 'text', placeholder: 'weather' }] },
  { key: 'qr', desc: 'iOS pairing QR', ui: 'Missing', voice: 'No', support: 'missing', priority: 'low', notes: 'Future device pairing flow' },
  { key: 'secrets', desc: 'Secrets management', ui: 'Keys tab', voice: 'No', support: 'working', priority: '', notes: 'Maps directly to API keys', params: [{ key: 'action', label: 'Action', type: 'select', options: ['list', 'get', 'set', 'remove'] }, { key: 'name', label: 'Secret name', type: 'text', placeholder: 'ELEVEN_LABS_TOKEN' }, { key: 'value', label: 'Secret value', type: 'password', placeholder: 'sk_live_...' }] },
  { key: 'sessions', desc: 'List conversations', ui: 'History dropdown', voice: 'No', support: 'partial', priority: 'med', notes: 'Needs a fuller browser', params: [{ key: 'limit', label: 'Limit', type: 'number', placeholder: '25' }] },
  { key: 'skills', desc: 'List or inspect skills', ui: 'Missing', voice: 'No', support: 'missing', priority: 'med', notes: 'Good candidate for documentation UI' },
  { key: 'status', desc: 'Channel health', ui: 'Dashboard stats', voice: 'Ask by voice', support: 'working', priority: '', notes: 'Basic status is already exposed' },
  { key: 'system', desc: 'Events and heartbeat', ui: 'Missing', voice: 'No', support: 'internal', priority: 'low', notes: 'Infrastructure command' },
  { key: 'tui', desc: 'Terminal UI', ui: 'Terminal tab', voice: 'No', support: 'working', priority: '', notes: 'Inline terminal already exists' },
  { key: 'webhooks', desc: 'Webhook integrations', ui: 'Missing', voice: 'No', support: 'missing', priority: 'high', notes: 'Important missing integration point', params: [{ key: 'action', label: 'Action', type: 'select', options: ['list', 'add', 'remove'] }, { key: 'event', label: 'Event', type: 'select', options: ['booking.created', 'call.missed', 'contact.created'] }, { key: 'url', label: 'Webhook URL', type: 'text', placeholder: 'https://example.com/webhook' }] },
  { key: 'backup', desc: 'Backup archives', ui: 'Missing', voice: 'No', support: 'missing', priority: 'med', notes: 'Future export path', params: [{ key: 'name', label: 'Archive name', type: 'text', placeholder: 'backup-2026-03-31' }] },
  { key: 'browser', desc: 'Manage browser automation', ui: 'Missing', voice: 'No', support: 'missing', priority: 'low', notes: 'Future agent browser automation' },
  { key: 'sandbox', desc: 'Sandbox containers', ui: 'Missing', voice: 'No', support: 'missing', priority: 'low', notes: 'Advanced isolation command' },
  { key: 'security', desc: 'Security audits', ui: 'Security tab only', voice: 'No', support: 'partial', priority: 'med', notes: 'UI is lighter than the CLI intent' },
  { key: 'approvals', desc: 'Execution approvals', ui: 'Missing', voice: 'No', support: 'missing', priority: 'med', notes: 'Future approval workflow' },
  { key: 'acp', desc: 'Agent Control Protocol', ui: 'Missing', voice: 'No', support: 'missing', priority: 'low', notes: 'Advanced orchestration' },
  { key: 'nodes', desc: 'Node management', ui: 'Missing', voice: 'No', support: 'missing', priority: 'low', notes: 'Multi-node deployment' },
  { key: 'pairing', desc: 'DM pairing', ui: 'Missing', voice: 'No', support: 'missing', priority: 'low', notes: 'Secure channel pairing', params: [{ key: 'phone', label: 'Phone', type: 'phone', placeholder: '+16505551212' }] },
  { key: 'update', desc: 'Update OpenClaw', ui: 'Missing', voice: 'No', support: 'missing', priority: 'low', notes: 'Container update flow', params: [{ key: 'channel', label: 'Channel', type: 'select', options: ['stable', 'canary'] }] },
];

const shellQuote = (value) => `'${String(value || '').replace(/'/g, `'\\''`)}'`;

const buildOpenClawCommand = (spec, values) => {
  const v = values || {};
  switch (spec.key) {
    case 'agent':
      return v.prompt ? `openclaw agent ${shellQuote(v.prompt)}` : 'openclaw agent';
    case 'config':
      if (v.action === 'set' && v.name) return `openclaw config set ${v.name}${v.value ? ` ${shellQuote(v.value)}` : ''}`;
      if (v.action === 'get' && v.name) return `openclaw config get ${v.name}`;
      return 'openclaw config';
    case 'channels':
      return `openclaw channels${v.action ? ` ${v.action}` : ''}${v.channel ? ` ${v.channel}` : ''}`.trim();
    case 'cron':
      if (v.action === 'add' && v.schedule && v.task) return `openclaw cron add ${shellQuote(v.schedule)} ${shellQuote(v.task)}`;
      if (v.action === 'remove' && v.schedule) return `openclaw cron remove ${shellQuote(v.schedule)}`;
      return `openclaw cron${v.action ? ` ${v.action}` : ''}`.trim();
    case 'directory':
    case 'memory':
    case 'skills':
      return v.query ? `openclaw ${spec.key} ${shellQuote(v.query)}` : `openclaw ${spec.key}`;
    case 'hooks':
    case 'webhooks':
      if (v.action === 'add' && v.event && (v.target || v.url)) return `openclaw ${spec.key} add ${v.event} ${shellQuote(v.target || v.url)}`;
      if (v.action === 'remove' && v.event) return `openclaw ${spec.key} remove ${v.event}`;
      return `openclaw ${spec.key}${v.action ? ` ${v.action}` : ''}`.trim();
    case 'logs':
      return v.tail ? `openclaw logs --tail ${v.tail}` : 'openclaw logs';
    case 'message':
      if (v.action === 'send') {
        return `openclaw message send${v.channel ? ` --channel ${v.channel}` : ''}${v.to ? ` --to ${shellQuote(v.to)}` : ''}${v.text ? ` ${shellQuote(v.text)}` : ''}`.trim();
      }
      return 'openclaw message';
    case 'models':
      return v.action === 'use' && v.model ? `openclaw models use ${shellQuote(v.model)}` : 'openclaw models';
    case 'plugins':
      return `openclaw plugins${v.action ? ` ${v.action}` : ''}${v.plugin ? ` ${shellQuote(v.plugin)}` : ''}`.trim();
    case 'secrets':
      if (v.action === 'set' && v.name) return `openclaw secrets set ${v.name}${v.value ? ` ${shellQuote(v.value)}` : ''}`;
      if ((v.action === 'get' || v.action === 'remove') && v.name) return `openclaw secrets ${v.action} ${v.name}`;
      return 'openclaw secrets';
    case 'sessions':
      return v.limit ? `openclaw sessions --limit ${v.limit}` : 'openclaw sessions';
    case 'backup':
      return v.name ? `openclaw backup ${shellQuote(v.name)}` : 'openclaw backup';
    case 'pairing':
      return v.phone ? `openclaw pairing ${shellQuote(v.phone)}` : 'openclaw pairing';
    case 'update':
      return v.channel ? `openclaw update --channel ${v.channel}` : 'openclaw update';
    default:
      return `openclaw ${spec.key}`;
  }
};

const OpenClawSubTab = ({ status }) => {
  const [terminalState, setTerminalState] = useState({ loading: true, ok: false, commands: [], error: '' });
  const [supportFilter, setSupportFilter] = useState('all');
  const [selectedCommandKey, setSelectedCommandKey] = useState('agent');
  const [formValues, setFormValues] = useState({});
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    authFetch('/api/aiassistant/terminal/commands')
      .then(r => r.ok ? r.json() : r.json().catch(() => ({})).then(data => Promise.reject(new Error(data.error || `HTTP ${r.status}`))))
      .then(data => {
        if (cancelled) return;
        setTerminalState({ loading: false, ok: true, commands: data.commands || [], error: '' });
      })
      .catch(err => {
        if (cancelled) return;
        setTerminalState({ loading: false, ok: false, commands: [], error: err.message || 'Terminal command probe failed' });
      });
    return () => { cancelled = true; };
  }, []);

  const filteredCommands = OPENCLAW_COMMAND_SPECS.filter(cmd => supportFilter === 'all' ? true : cmd.support === supportFilter);
  const selectedSpec = OPENCLAW_COMMAND_SPECS.find(cmd => cmd.key === selectedCommandKey) || OPENCLAW_COMMAND_SPECS[0];
  const detectedCommandNames = new Set((terminalState.commands || [])
    .map(c => (c.cmd || '').trim())
    .filter(c => c.indexOf('openclaw ') === 0)
    .map(c => c.replace(/^openclaw\s+/, '').split(/\s+/)[0]));
  const generatedCommand = buildOpenClawCommand(selectedSpec, formValues[selectedSpec.key] || {});

  const updateValue = (commandKey, fieldKey, value) => {
    setFormValues(prev => ({
      ...prev,
      [commandKey]: { ...(prev[commandKey] || {}), [fieldKey]: value }
    }));
  };

  const copyCommand = async () => {
    try {
      await navigator.clipboard.writeText(generatedCommand);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (e) {}
  };

  return (
    <div className="settings-panel">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:16,marginBottom:18,flexWrap:'wrap'}}>
        <div>
          <h4 style={{margin:'0 0 4px',fontSize:14}}>OpenClaw Commands</h4>
          <p style={{color:'#888',fontSize:12,margin:0}}>Map the OpenClaw CLI to what already exists in NumFree, then generate a pasteable terminal command with the right parameter fields.</p>
        </div>
        <div style={{minWidth:260,background:'#f9f9fb',border:'1px solid #e8e8e8',borderRadius:8,padding:'10px 12px'}}>
          <div style={{fontWeight:600,fontSize:12,marginBottom:6}}>Terminal status</div>
          {terminalState.loading ? (
            <div style={{fontSize:12,color:'#666'}}>Checking container terminal…</div>
          ) : terminalState.ok ? (
            <>
              <div style={{fontSize:12,color:'#2e7d32'}}>Ready {status?.containerId ? `for ${status.containerId}` : ''}</div>
              <div style={{fontSize:11,color:'#666',marginTop:4}}>{terminalState.commands.length} palette commands detected from the live container.</div>
            </>
          ) : (
            <>
              <div style={{fontSize:12,color:'#c62828'}}>Probe failed</div>
              <div style={{fontSize:11,color:'#666',marginTop:4}}>{terminalState.error || 'The terminal websocket may still work, but the command probe did not return data.'}</div>
            </>
          )}
        </div>
      </div>

      <div style={{display:'flex',gap:20,alignItems:'flex-start',flexWrap:'wrap'}}>
        <div style={{flex:'0 0 360px',minWidth:300}}>
          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:10}}>
            {[
              ['all', 'All'],
              ['working', 'Working'],
              ['partial', 'Partial'],
              ['missing', 'Missing'],
              ['internal', 'Internal']
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setSupportFilter(value)}
                style={{padding:'6px 10px',borderRadius:999,border:`1px solid ${supportFilter===value?'#1E203F':'#ddd'}`,background:supportFilter===value?'#f0f1f8':'#fff',fontSize:11,fontWeight:600,cursor:'pointer'}}
              >
                {label}
              </button>
            ))}
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:520,overflowY:'auto'}}>
            {filteredCommands.map(cmd => {
              const selected = cmd.key === selectedCommandKey;
              const detected = detectedCommandNames.has(cmd.key);
              const supportColor = cmd.support === 'working' ? '#2e7d32' : cmd.support === 'partial' ? '#ef6c00' : cmd.support === 'internal' ? '#546e7a' : '#c62828';
              return (
                <button
                  key={cmd.key}
                  type="button"
                  onClick={() => setSelectedCommandKey(cmd.key)}
                  style={{textAlign:'left',padding:'10px 12px',borderRadius:10,border:`1px solid ${selected?'#1E203F':'#e8e8e8'}`,background:selected?'#f0f1f8':'#fff',cursor:'pointer'}}
                >
                  <div style={{display:'flex',justifyContent:'space-between',gap:8,alignItems:'center'}}>
                    <div style={{fontWeight:600,fontSize:13}}>openclaw {cmd.key}</div>
                    <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap',justifyContent:'flex-end'}}>
                      {cmd.priority ? <span style={{fontSize:10,fontWeight:700,color:'#fff',background:cmd.priority==='high'?'#c62828':'#ef6c00',padding:'2px 6px',borderRadius:999}}>{cmd.priority.toUpperCase()}</span> : null}
                      {detected ? <span style={{fontSize:10,fontWeight:700,color:'#fff',background:'#2e7d32',padding:'2px 6px',borderRadius:999}}>DETECTED</span> : null}
                      <span style={{fontSize:10,fontWeight:700,color:supportColor,background:`${supportColor}14`,padding:'2px 6px',borderRadius:999}}>{cmd.support.toUpperCase()}</span>
                    </div>
                  </div>
                  <div style={{fontSize:11,color:'#666',marginTop:4}}>{cmd.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{flex:'1 1 520px',minWidth:320}}>
          <div style={{border:'1px solid #e8e8e8',borderRadius:12,padding:16,background:'#fff'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12,marginBottom:10,flexWrap:'wrap'}}>
              <div>
                <h4 style={{margin:'0 0 4px',fontSize:15}}>openclaw {selectedSpec.key}</h4>
                <p style={{margin:0,fontSize:12,color:'#666'}}>{selectedSpec.desc}</p>
              </div>
              <button type="button" onClick={copyCommand} style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:8,background:'#fff',cursor:'pointer',fontSize:12,fontWeight:600}}>
                {copied ? 'Copied' : 'Copy Command'}
              </button>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))',gap:10,marginBottom:14}}>
              <div style={{background:'#f9f9fb',border:'1px solid #e8e8e8',borderRadius:8,padding:10}}>
                <div style={{fontSize:11,color:'#888'}}>Portal</div>
                <div style={{fontSize:12,fontWeight:600}}>{selectedSpec.ui}</div>
              </div>
              <div style={{background:'#f9f9fb',border:'1px solid #e8e8e8',borderRadius:8,padding:10}}>
                <div style={{fontSize:11,color:'#888'}}>Voice</div>
                <div style={{fontSize:12,fontWeight:600}}>{selectedSpec.voice}</div>
              </div>
              <div style={{background:'#f9f9fb',border:'1px solid #e8e8e8',borderRadius:8,padding:10}}>
                <div style={{fontSize:11,color:'#888'}}>Notes</div>
                <div style={{fontSize:12,fontWeight:600}}>{selectedSpec.notes}</div>
              </div>
            </div>

            {(selectedSpec.params || []).length > 0 && (
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))',gap:10,marginBottom:14}}>
                {selectedSpec.params.map(field => {
                  const value = (formValues[selectedSpec.key] || {})[field.key] || '';
                  const common = {
                    value,
                    onChange: (e) => updateValue(selectedSpec.key, field.key, e.target.value),
                    style: {width:'100%',padding:'8px 10px',border:'1px solid #ddd',borderRadius:8,fontSize:12,boxSizing:'border-box'}
                  };
                  return (
                    <label key={field.key} style={{display:'flex',flexDirection:'column',gap:6}}>
                      <span style={{fontSize:11,fontWeight:600,color:'#555'}}>{field.label}</span>
                      {field.type === 'select' ? (
                        <select {...common}>
                          <option value="">Select…</option>
                          {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      ) : field.type === 'textarea' ? (
                        <textarea {...common} rows={3} placeholder={field.placeholder || ''} />
                      ) : (
                        <input {...common} type={field.type === 'password' ? 'password' : field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'} placeholder={field.placeholder || ''} />
                      )}
                    </label>
                  );
                })}
              </div>
            )}

            <div style={{fontSize:11,fontWeight:700,color:'#666',marginBottom:6}}>Pasteable terminal command</div>
            <pre style={{margin:0,padding:'12px 14px',background:'#1a1b26',color:'#c0caf5',borderRadius:10,overflowX:'auto',fontSize:12,lineHeight:1.5}}>$ {generatedCommand}</pre>
            <div style={{fontSize:11,color:'#888',marginTop:8}}>
              This is a starter command built from the current OpenClaw map. Paste it into the Terminal tab and edit further if the container CLI needs extra flags.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ViewIconFull = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="4" width="18" height="5" rx="1.5" />
    <rect x="3" y="10" width="18" height="5" rx="1.5" />
    <rect x="3" y="16" width="18" height="5" rx="1.5" />
  </svg>
);

const ViewIconCompact = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <line x1="4" y1="7" x2="20" y2="7" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="17" x2="20" y2="17" />
    <circle cx="7" cy="7" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="7" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="7" cy="17" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

// Settings Tab
const SETTINGS_SUBTABS = ['Agent', 'Voice', 'Numbers', 'Access', 'Email', 'Keys', 'Advanced', 'Security'];

const ACCESS_RESOURCES = [
  { key: 'contacts', label: 'Contacts', allow: { read: true, write: true, delete: true } },
  { key: 'calendar', label: 'Calendar', allow: { read: true, write: true, delete: true } },
  { key: 'calendar_availability', label: 'Calendar Availability', allow: { read: true, write: true, delete: false } },
  { key: 'notes', label: 'Notes', allow: { read: true, write: true, delete: true } },
  { key: 'tasks', label: 'Tasks', allow: { read: true, write: true, delete: true } },
  { key: 'sms', label: 'SMS', allow: { read: true, write: true, delete: false } },
  { key: 'dictation', label: 'Dictation', allow: { read: true, write: true, delete: false } },
  { key: 'linktime', label: 'LinkTime', allow: { read: true, write: true, delete: false } },
  { key: 'web', label: 'Web Search & Research', allow: { read: true, write: false, delete: false } },
  { key: 'travel', label: 'Travel', allow: { read: true, write: false, delete: false } },
  { key: 'email', label: 'Email', allow: { read: true, write: true, delete: true }, ownerOnly: true },
  { key: 'system', label: 'System', allow: { read: true, write: true, delete: false } },
  { key: 'conversations', label: 'Conversations', allow: { read: true, write: false, delete: false } },
];

const DEFAULT_ACCESS = ACCESS_RESOURCES.reduce((acc, r) => {
  acc[r.key] = { read: false, write: false, delete: false };
  return acc;
}, {});

const DEFAULT_GUEST_ACCESS = ACCESS_RESOURCES.reduce((acc, r) => {
  acc[r.key] = { read: false, write: false, delete: false };
  return acc;
}, {});

// Access tab with Rights + Tune sub-tabs
const AccessWithTuneTab = ({ access, guestAccess, toggleAccess, toggleGuestAccess, handleSave, saving }) => {
  const [accessSubTab, setAccessSubTab] = useState('Rights');
  const [tuneDisplayMode, setTuneDisplayMode] = useState('full');
  return (
    <div className="tab-settings">
      <div style={{display:'flex',gap:4,padding:'8px 16px',borderBottom:'1px solid #e8e8e8',background:'#fafafa'}}>
        {['Rights', 'Tune'].map(t => (
          <button key={t} onClick={() => setAccessSubTab(t)}
            style={{background:accessSubTab===t?'#fff':'none',border:'none',padding:'8px 14px',fontSize:12,fontWeight:500,color:accessSubTab===t?'#1E203F':'#5a6480',cursor:'pointer',borderBottom:accessSubTab===t?'2px solid #1E203F':'2px solid transparent',marginBottom:-1}}>
            {t}
          </button>
        ))}
      </div>
      {accessSubTab === 'Rights' && (
        <div className="settings-panel">
          <p className="access-desc">Control what your assistant can access. <strong>Owner</strong> = when you interact. <strong>Guest</strong> = when external callers interact.</p>
          <table className="access-table">
            <thead>
              <tr>
                <th rowSpan={2}>Resource</th>
                <th colSpan={3} className="access-group-header">Owner</th>
                <th colSpan={3} className="access-group-header guest">Guest</th>
              </tr>
              <tr><th>R</th><th>W</th><th>D</th><th>R</th><th>W</th><th>D</th></tr>
            </thead>
            <tbody>
              {ACCESS_RESOURCES.map(r => (
                <tr key={r.key}>
                  <td className="access-label">{r.label}</td>
                  <td>{r.allow.read ? <input type="checkbox" checked={access[r.key]?.read || false} onChange={() => toggleAccess(r.key, 'read')} /> : <span className="access-na">—</span>}</td>
                  <td>{r.allow.write ? <input type="checkbox" checked={access[r.key]?.write || false} onChange={() => toggleAccess(r.key, 'write')} /> : <span className="access-na">—</span>}</td>
                  <td>{r.allow.delete ? <input type="checkbox" checked={access[r.key]?.delete || false} onChange={() => toggleAccess(r.key, 'delete')} /> : <span className="access-na">—</span>}</td>
                  <td>{r.ownerOnly ? <span className="access-na">—</span> : r.allow.read ? <input type="checkbox" checked={guestAccess[r.key]?.read || false} onChange={() => toggleGuestAccess(r.key, 'read')} /> : <span className="access-na">—</span>}</td>
                  <td>{r.ownerOnly ? <span className="access-na">—</span> : r.allow.write ? <input type="checkbox" checked={guestAccess[r.key]?.write || false} onChange={() => toggleGuestAccess(r.key, 'write')} /> : <span className="access-na">—</span>}</td>
                  <td>{r.ownerOnly ? <span className="access-na">—</span> : r.allow.delete ? <input type="checkbox" checked={guestAccess[r.key]?.delete || false} onChange={() => toggleGuestAccess(r.key, 'delete')} /> : <span className="access-na">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="btn-save" onClick={() => handleSave()} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      )}
      {accessSubTab === 'Tune' && (
        <div>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 16px 0'}}>
            <div style={{fontSize:12,color:'#5a6480'}}>
              Full shows reasons and fixes. Compact shows the quick enable list.
            </div>
            <div style={{display:'flex',gap:6}}>
              <button
                onClick={() => setTuneDisplayMode('full')}
                style={{
                  display:'inline-flex',alignItems:'center',gap:6,
                  padding:'6px 10px',borderRadius:7,cursor:'pointer',
                  border:'1px solid ' + (tuneDisplayMode === 'full' ? '#1E203F' : '#d7dbe7'),
                  background:tuneDisplayMode === 'full' ? '#1E203F' : '#fff',
                  color:tuneDisplayMode === 'full' ? '#fff' : '#1E203F',
                  fontSize:12,fontWeight:600
                }}
              >
                <ViewIconFull />
                Full
              </button>
              <button
                onClick={() => setTuneDisplayMode('compact')}
                style={{
                  display:'inline-flex',alignItems:'center',gap:6,
                  padding:'6px 10px',borderRadius:7,cursor:'pointer',
                  border:'1px solid ' + (tuneDisplayMode === 'compact' ? '#1E203F' : '#d7dbe7'),
                  background:tuneDisplayMode === 'compact' ? '#1E203F' : '#fff',
                  color:tuneDisplayMode === 'compact' ? '#fff' : '#1E203F',
                  fontSize:12,fontWeight:600
                }}
              >
                <ViewIconCompact />
                Compact
              </button>
            </div>
          </div>
          {tuneDisplayMode === 'full' ? <TuningSubTab /> : <AccessLogSubTab />}
        </div>
      )}
    </div>
  );
};

// Numbers SubTab — per-number AI enable/disable
const normalizeManagedNumber = (value) => {
  var raw = value && typeof value === 'object' ? (value.number || value.phoneNumber || value.value || '') : value;
  return String(raw || '').replace(/\D/g, '');
};

const NumbersSubTab = ({ config, status, user }) => {
  var initial = useMemo(function() {
    var values = (config?.assignedNumbers || []).map(normalizeManagedNumber).filter(Boolean);
    // Fallback to phoneNumber if no assignedNumbers set
    if (values.length === 0 && config?.phoneNumber) {
      values = [normalizeManagedNumber(config.phoneNumber)].filter(Boolean);
    }
    return values;
  }, [config?.assignedNumbers, config?.phoneNumber]);
  var [assigned, setAssigned] = useState(initial);
  var [confirmNum, setConfirmNum] = useState(null);
  var [savingNumbers, setSavingNumbers] = useState(false);
  var freenumbers = (user?.freenumbers || []).map(function(n) {
    var value = normalizeManagedNumber(n);
    var raw = n && typeof n === 'object' ? (n.number || n.phoneNumber || n.value || value) : n;
    return value ? { value: value, label: String(raw || value) } : null;
  }).filter(Boolean);

  useEffect(() => {
    setAssigned(initial);
  }, [initial]);

  var toggleNumber = function(num) {
    var next;
    if (assigned.indexOf(num) >= 0) {
      next = assigned.filter(function(n) { return n !== num; });
      setAssigned(next);
      saveAssignments(next);
    } else {
      setConfirmNum(num);
    }
  };

  var confirmAssign = function() {
    if (confirmNum && assigned.indexOf(confirmNum) < 0) {
      var next = assigned.concat(confirmNum);
      setAssigned(next);
      saveAssignments(next);
    }
    setConfirmNum(null);
  };

  var saveAssignments = function(nums) {
    setSavingNumbers(true);
    authFetch('/api/aiassistant/config', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assignedNumbers: nums })
    }).finally(function() { setSavingNumbers(false); });
  };

  if (freenumbers.length === 0) return <div className="settings-panel"><p style={{color:'#999',fontSize:13}}>No phone numbers yet. <a href="/numbers">Get a number</a>.</p></div>;

  return (
    <div className="settings-panel">
      <p style={{color:'#666',fontSize:12,marginBottom:12}}>Choose which numbers your AI assistant can answer. When enabled, the AI will handle incoming calls and texts on that number.</p>
      <div className="numbers-list">
          {freenumbers.map(function(row) {
            var num = row.value;
            var isAssigned = assigned.indexOf(num) >= 0;
            return (
              <label key={num} className="numbers-list-row">
                <input type="checkbox" checked={isAssigned} disabled={savingNumbers} onChange={function() { toggleNumber(num); }} />
                <span className="numbers-list-num">{row.label}</span>
                <span className="numbers-list-status">{savingNumbers ? 'Saving...' : (isAssigned ? 'AI Active' : 'Off')}</span>
              </label>
            );
          })}
      </div>

      {confirmNum && (
        <div className="confirm-overlay" onClick={function() { setConfirmNum(null); }}>
          <div className="confirm-dialog" onClick={function(e) { e.stopPropagation(); }}>
            <h4>Enable AI for {confirmNum}?</h4>
            <p>Your AI assistant will answer incoming calls and texts on this number. People calling this number will talk to your agent. You can disable this anytime.</p>
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={function() { setConfirmNum(null); }}>Cancel</button>
              <button className="btn-confirm" onClick={confirmAssign}>Enable AI</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SettingsTab = ({ config, onSave, status, applyTemplate: initialTemplate, user }) => {
  const c = config?.config || {};
  const hasConfiguredElevenLabs = !!(c.elevenlabsApiKey || (c.envKeys && c.envKeys.ELEVEN_LABS_TOKEN));
  const initialTtsEngine = c.ttsEngine === 'mimo' ? 'mimo' : (c.ttsEngine === 'elevenlabs' ? 'elevenlabs' : (hasConfiguredElevenLabs ? 'elevenlabs' : 'edge'));
  const [subTab, setSubTab] = useState('Agent');
  const [name, setName] = useState(c.agentName || 'AI Assistant');
  const [ownerName, setOwnerName] = useState(c.ownerName || '');
  const [greeting, setGreeting] = useState(c.greeting || '');
  const [prompt, setPrompt] = useState(c.systemPrompt || '');
  const [voice, setVoice] = useState(c.voiceId || 'sarah');
  const [ttsEngine, setTtsEngine] = useState(initialTtsEngine);
  const [language, setLanguage] = useState(c.language || 'en-US');
  const [llmModel, setLlmModel] = useState(c.llmModel || 'anthropic/claude-haiku-4.5');
  const [modelCatalog, setModelCatalog] = useState(FALLBACK_MODEL_CATALOG);
  const [userTier, setUserTier] = useState('free');
  const [allowedModels, setAllowedModels] = useState(null); // null = all allowed
  const [tierFeatures, setTierFeatures] = useState({});
  const [freeModel, setFreeModel] = useState('cloudflare/llama-3.3-70b');
  const [timezone, setTimezone] = useState(c.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles');
  const [allowlist, setAllowlist] = useState((config?.transferAllowlist || []).map(e => (e && typeof e === 'object') ? (e.number || '') : e).filter(Boolean).join('\n'));
  const [ownerNums, setOwnerNums] = useState((config?.ownerNumbers || []).join('\n'));
  const [access, setAccess] = useState({ ...DEFAULT_ACCESS, ...(c.access || {}) });
  const [guestAccess, setGuestAccess] = useState({ ...DEFAULT_GUEST_ACCESS, ...(c.guestAccess || {}) });
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateSearch, setTemplateSearch] = useState('');
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const isFreeTier = useMemo(() => userTier === 'free' || userTier === 'demo', [userTier]);

  useEffect(() => {
    authFetch('/api/marketplace/prompts?sort=downloads&limit=50')
      .then(r => r.ok ? r.json() : []).then(setTemplates).catch(() => {});
  }, []);

  useEffect(() => {
    authFetch('/api/aiassistant/models')
      .then(r => r.ok ? r.json() : null)
      .then((data) => {
        const models = Array.isArray(data?.models) && data.models.length ? data.models : FALLBACK_MODEL_CATALOG;
        const merged = Array.isArray(models) ? [...models] : FALLBACK_MODEL_CATALOG;
        setModelCatalog(merged);
        if (data?.tier) setUserTier(data.tier);
        if (data?.allowed_models !== undefined) setAllowedModels(data.allowed_models);
        if (data?.features) setTierFeatures(data.features);
        if (data?.free_model) setFreeModel(data.free_model);
      })
      .catch(() => setModelCatalog(FALLBACK_MODEL_CATALOG));
  }, []);

  useEffect(() => {
    if (isFreeTier) {
      setLlmModel(normalizeCatalogModel(freeModel, modelCatalog));
    } else {
      setLlmModel(normalizeCatalogModel(c.llmModel || 'anthropic/claude-haiku-4.5', modelCatalog));
    }
  }, [c.llmModel, modelCatalog, isFreeTier, freeModel]);

  // Auto-apply template from marketplace navigation
  useEffect(() => {
    if (initialTemplate) {
      if (initialTemplate.soul || initialTemplate.systemPrompt) setPrompt(initialTemplate.soul || initialTemplate.systemPrompt);
      if (initialTemplate.greeting) setGreeting(initialTemplate.greeting);
      if (initialTemplate.voiceId) setVoice(initialTemplate.voiceId);
    }
  }, [initialTemplate]);

  const filteredCatalog = useMemo(() => {
    // Same visibility rule as the dashboard picker: deprecated (superseded)
    // models never render; noTools models can't run the agent's tools.
    const visible = (m) => m && m.status !== 'deprecated' && !m.noTools;
    if (isFreeTier) {
      const normalizedFreeModel = normalizeCatalogModel(freeModel, modelCatalog);
      const freeEntry = (modelCatalog || []).find(m => {
        if (!visible(m)) return false;
        if (m.id === normalizedFreeModel || m.id === freeModel) return true;
        if ((m.aliases || []).includes(freeModel)) return true;
        return Object.values(m.provider_variants || {}).includes(freeModel);
      });
      return freeEntry ? [freeEntry] : [];
    }
    if (allowedModels === null) return (modelCatalog || []).filter(visible);
    if (!Array.isArray(allowedModels) || !allowedModels.length) return (modelCatalog || []).filter(visible);
    return (modelCatalog || []).filter(m => visible(m) && (allowedModels.includes(m.id) || (m.aliases || []).some(a => allowedModels.includes(a))));
  }, [modelCatalog, allowedModels, isFreeTier, freeModel]);

  const modelGroups = useMemo(() => {
    return (filteredCatalog).reduce((acc, model) => {
      const family = model.family || 'other';
      if (!acc[family]) acc[family] = [];
      acc[family].push(model);
      return acc;
    }, {});
  }, [filteredCatalog]);

  const voices = [
    { id: 'sarah', label: 'Sarah (Warm, American)' },
    { id: 'james', label: 'James (Professional, American)' },
    { id: 'sofia', label: 'Sofia (Friendly, American)' },
    { id: 'george', label: 'George (British, Calm)' },
    { id: 'lily', label: 'Lily (Energetic, American)' },
  ];

  const applyTemplate = (t) => {
    if (t.soul || t.systemPrompt) setPrompt(t.soul || t.systemPrompt);
    if (t.greeting) setGreeting(t.greeting);
    if (t.voiceId) setVoice(t.voiceId);
    if (t.name && !name) setName(t.name);
    setShowTemplatePicker(false);
  };

  const filteredTemplates = templates.filter(t => {
    if (!templateSearch) return true;
    const q = templateSearch.toLowerCase();
    return (t.name || '').toLowerCase().includes(q) || (t.category || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q);
  });

  const handleSave = async (extraFields) => {
    setSaving(true);
    try {
      const payload = {
        agentName: name, ownerName, greeting, systemPrompt: prompt, voiceId: voice, ttsEngine, language, llmModel, timezone,
        access: JSON.parse(JSON.stringify(access)),
        guestAccess: JSON.parse(JSON.stringify(guestAccess)),
        transferAllowlist: allowlist.split('\n').map(n => n.trim()).filter(Boolean).map(n => {
          const ex = (config?.transferAllowlist || []).find(e => e && (e.number === n || e === n));
          return { number: n, expiresAt: (ex && ex.expiresAt) || '2099-12-31T00:00:00.000Z' };
        }),
        ownerNumbers: ownerNums.split('\n').map(n => n.trim()).filter(Boolean),
        ...(extraFields || {})
      };
      await onSave(payload);
    } catch (e) { console.error('Save error:', e); }
    setSaving(false);
  };

  const toggleAccess = (resource, permission) => {
    setAccess(prev => ({
      ...prev,
      [resource]: { ...prev[resource], [permission]: !prev[resource][permission] }
    }));
  };

  const toggleGuestAccess = (resource, permission) => {
    setGuestAccess(prev => ({
      ...prev,
      [resource]: { ...prev[resource], [permission]: !prev[resource][permission] }
    }));
  };



  return (
    <div className="tab-settings">
      <div className="settings-subtabs">
        {SETTINGS_SUBTABS.map(st => (
          <button key={st} className={`subtab-btn ${subTab === st ? 'active' : ''}`} onClick={() => setSubTab(st)}>{st}</button>
        ))}
      </div>

      {subTab === 'Agent' && (
        <div className="settings-panel">
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16}}>
            <h4 style={{margin:0,fontSize:14}}>Personality</h4>
            <button onClick={() => setShowTemplatePicker(!showTemplatePicker)}
              style={{background:'none',border:'1px solid #ccc',borderRadius:6,padding:'3px 10px',fontSize:12,color:'#546e7a',cursor:'pointer'}}>
              {showTemplatePicker ? 'Close' : 'Browse Templates'}
            </button>
          </div>

          {showTemplatePicker && (
            <div style={{background:'#f9f9fb',border:'1px solid #e0e0e0',borderRadius:8,padding:12,marginBottom:16}}>
              <input type="text" value={templateSearch} onChange={e => setTemplateSearch(e.target.value)}
                placeholder="Search templates..." style={{width:'100%',padding:'8px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:13,marginBottom:10,boxSizing:'border-box'}} />
              <div style={{maxHeight:240,overflowY:'auto',display:'flex',flexDirection:'column',gap:6}}>
                {filteredTemplates.length === 0 && <p style={{color:'#999',fontSize:12,textAlign:'center',padding:12}}>No templates found</p>}
                {filteredTemplates.map(t => (
                  <div key={t.id} onClick={() => applyTemplate(t)} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:'#fff',border:'1px solid #e8e8e8',borderRadius:6,cursor:'pointer',transition:'border-color 0.15s'}}
                    onMouseEnter={e => e.currentTarget.style.borderColor='#1E203F'} onMouseLeave={e => e.currentTarget.style.borderColor='#e8e8e8'}>
                    <span style={{fontSize:20}}>{({'general':'🤖','real-estate':'🏠','medical':'🏥','legal':'⚖️','restaurant':'🍽️','retail':'🛍️','fitness':'🏋️','automotive':'🚗','tech-support':'💻','salon-spa':'💇','finance':'💰','education':'📚'})[t.category] || '📦'}</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:600,fontSize:13}}>{t.name}</div>
                      <div style={{fontSize:11,color:'#888',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.description}</div>
                    </div>
                    <div style={{fontSize:11,color:'#999',flexShrink:0}}>
                      {'⭐'.repeat(Math.round(t.avgRating || 0))} {t.downloads || 0}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Agent Name</label>
            <input value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Your Name</label>
            <p style={{color:'#888',fontSize:12,margin:'0 0 6px'}}>Used to greet you personally when you call in from your registered numbers.</p>
            <input value={ownerName} onChange={e => setOwnerName(e.target.value)} placeholder="e.g. Nick" />
          </div>
          <div className="form-group">
            <label>Greeting Message</label>
            <p style={{color:'#888',fontSize:12,margin:'0 0 6px'}}>Default greeting for guests/visitors. You get a personalized greeting when calling from your own numbers.</p>
            <input value={greeting} onChange={e => setGreeting(e.target.value)} placeholder="Hi, I'm Luna! How can I help?" />
          </div>
          <div className="form-group">
            <label>Timezone</label>
            <p style={{color:'#888',fontSize:12,margin:'0 0 6px'}}>Used for all scheduling, time references, and calendar operations.</p>
            <div style={{display:'flex',gap:6}}>
              <select value={timezone} onChange={e => setTimezone(e.target.value)}
                style={{flex:1,padding:'8px 10px',border:'1px solid #ddd',borderRadius:8,fontSize:13}}>
                <optgroup label="US">
                  <option value="America/New_York">Eastern (ET)</option>
                  <option value="America/Chicago">Central (CT)</option>
                  <option value="America/Denver">Mountain (MT)</option>
                  <option value="America/Los_Angeles">Pacific (PT)</option>
                  <option value="America/Anchorage">Alaska (AKT)</option>
                  <option value="Pacific/Honolulu">Hawaii (HT)</option>
                </optgroup>
                <optgroup label="Europe">
                  <option value="Europe/London">London (GMT/BST)</option>
                  <option value="Europe/Paris">Paris (CET)</option>
                  <option value="Europe/Berlin">Berlin (CET)</option>
                </optgroup>
                <optgroup label="Asia/Pacific">
                  <option value="Asia/Tokyo">Tokyo (JST)</option>
                  <option value="Asia/Shanghai">Shanghai (CST)</option>
                  <option value="Asia/Kolkata">India (IST)</option>
                  <option value="Australia/Sydney">Sydney (AEST)</option>
                </optgroup>
              </select>
              <button onClick={() => setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)}
                style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:8,fontSize:11,cursor:'pointer',background:'#f5f5f5',whiteSpace:'nowrap'}}>
                Detect
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Personality & Instructions</label>
            <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={6} placeholder="You are a helpful assistant..." />
          </div>

          <div className="form-group">
            <label>AI Model {(userTier === 'free' || userTier === 'demo') && <span style={{fontSize:11,color:'#888',fontWeight:'normal'}}> &mdash; Free plan (powered by Cloudflare)</span>}</label>
            <p style={{color:'#888',fontSize:12,margin:'0 0 6px'}}>Powers all channels: web, voice, SMS, WhatsApp.</p>
            {(userTier === 'free' || userTier === 'demo') && filteredCatalog.length <= 1 ? (
              <div>
                <div style={{padding:'10px 12px',background:'#f0f7ff',border:'1px solid #d0e3ff',borderRadius:8,fontSize:13,color:'#1a3a5c'}}>
                  {filteredCatalog[0]?.display_name || freeModel} <span style={{fontSize:11,color:'#888'}}>(free)</span>
                </div>
                <p style={{color:'#666',fontSize:11,margin:'6px 0 0'}}>Upgrade to choose from more models and enable the AI Agent.</p>
              </div>
            ) : (
              <select value={llmModel} onChange={e => setLlmModel(e.target.value)}
                style={{width:'100%',padding:'8px 10px',border:'1px solid #ddd',borderRadius:8,fontSize:13}}>
                {Object.entries(modelGroups).map(([family, models]) => (
                  <optgroup key={family} label={MODEL_FAMILY_LABELS[family] || family}>
                    {models.map((model) => {
                      const suffix = [];
                      if (MODEL_TIER_LABELS[model.tier]) suffix.push(MODEL_TIER_LABELS[model.tier]);
                      if (model.status === 'preview') suffix.push('preview');
                      return (
                        <option key={model.id} value={model.id}>
                          {model.display_name}{suffix.length ? ` (${suffix.join(', ')})` : ''}
                        </option>
                      );
                    })}
                  </optgroup>
                ))}
              </select>
            )}
          </div>

          <button className="btn-save" onClick={() => handleSave()} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      )}

      {subTab === 'Voice' && <VoiceSubTab
        ttsEngine={ttsEngine} setTtsEngine={setTtsEngine}
        voice={voice} setVoice={setVoice}
        language={language} setLanguage={setLanguage}
        config={config} saving={saving} onSave={handleSave}
      />}

      {subTab === 'Access' && (
        <AccessWithTuneTab access={access} guestAccess={guestAccess} toggleAccess={toggleAccess} toggleGuestAccess={toggleGuestAccess} handleSave={handleSave} saving={saving} />
      )}

      {subTab === 'Email' && <EmailSubTab />}

      {subTab === 'Keys' && <EnvKeysSubTab config={config} onSave={handleSave} saving={saving} />}

      {subTab === 'Advanced' && (
        <div className="settings-panel">
          <h4 style={{margin:'0 0 12px',fontSize:14}}>Advanced Configuration</h4>
          <p style={{color:'#888',fontSize:12,margin:'0 0 20px'}}>Fine-tune how OpenClaw processes requests. Defaults work well for most users.</p>

          <div className="settings-section-title">Response Tuning</div>
          <div className="form-group">
            <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
              <input
                type="checkbox"
                defaultChecked={!!c.softFailProbeEnabled}
                onChange={e => authFetch('/api/aiassistant/config', {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ softFailProbeEnabled: e.target.checked })
                })}
              />
              Soft-fail probe
            </label>
            <p style={{color:'#888',fontSize:11,margin:'6px 0 0'}}>Flags obvious stale-context misses and replaces them with a recovery reply instead of returning a wrong answer.</p>
          </div>
          <div className="form-group">
            <label>Temperature <span style={{fontWeight:400,color:'#888'}}>({c.temperature || 0.7})</span></label>
            <p style={{color:'#888',fontSize:11,margin:'0 0 4px'}}>Lower = more consistent, higher = more creative</p>
            <input type="range" min="0" max="1" step="0.1" value={c.temperature || 0.7}
              onChange={e => authFetch('/api/aiassistant/config', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ temperature: parseFloat(e.target.value) }) })}
              style={{width:'100%'}} />
            <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'#aaa'}}><span>Precise (0)</span><span>Creative (1)</span></div>
          </div>
          <div style={{display:'flex',gap:12}}>
            <div className="form-group" style={{flex:1}}>
              <label>Max tokens (web)</label>
              <input type="number" defaultValue={c.maxTokensWeb || 500} onBlur={e => authFetch("/api/aiassistant/config", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ maxTokensWeb: parseInt(e.target.value) || 500 }) })} />
            </div>
            <div className="form-group" style={{flex:1}}>
              <label>Max tokens (voice)</label>
              <input type="number" defaultValue={c.maxTokensVoice || 200} onBlur={e => authFetch("/api/aiassistant/config", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ maxTokensVoice: parseInt(e.target.value) || 200 }) })} />
            </div>
          </div>
          <div style={{display:'flex',gap:12}}>
            <div className="form-group" style={{flex:1}}>
              <label>History depth (web)</label>
              <p style={{color:'#888',fontSize:11,margin:'0 0 4px'}}>Past messages included as context</p>
              <input type="number" defaultValue={c.historyLimitWeb || 20} onBlur={e => authFetch("/api/aiassistant/config", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ historyLimitWeb: parseInt(e.target.value) || 20 }) })} />
            </div>
            <div className="form-group" style={{flex:1}}>
              <label>History depth (voice)</label>
              <input type="number" defaultValue={c.historyLimitVoice || 10} onBlur={e => authFetch("/api/aiassistant/config", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ historyLimitVoice: parseInt(e.target.value) || 10 }) })} />
            </div>
          </div>
          <div className="form-group">
            <label>Repetition penalty <span style={{fontWeight:400,color:'#888'}}>({c.frequencyPenalty || 0.6})</span></label>
            <input type="range" min="0" max="1.5" step="0.1" value={c.frequencyPenalty || 0.6}
              onChange={e => authFetch('/api/aiassistant/config', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ frequencyPenalty: parseFloat(e.target.value) }) })}
              style={{width:'100%'}} />
            <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'#aaa'}}><span>None (0)</span><span>Strong (1.5)</span></div>
          </div>

          <div className="settings-section-title">Channels</div>
          <p style={{color:'#888',fontSize:12,margin:'0 0 12px'}}>Enable or disable channels independently.</p>
          {['voice', 'sms', 'web', 'whatsapp'].map(ch => (
            <label key={ch} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 0',fontSize:13,cursor:'pointer'}}>
              <input type="checkbox" defaultChecked={c.channels ? c.channels[ch] !== false : true}
                onChange={e => { const upd = { ...(c.channels || {}), [ch]: e.target.checked }; authFetch('/api/aiassistant/config', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ channels: upd }) }); }}
                style={{width:16,height:16,accentColor:'#1E203F'}} />
              {ch.charAt(0).toUpperCase() + ch.slice(1)}
            </label>
          ))}

          <div className="settings-section-title">Behavior</div>
          <div className="form-group">
            <label>Fallback when container is down</label>
            <select defaultValue={c.fallbackMode || 'openrouter'} onChange={e => authFetch('/api/aiassistant/config', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ fallbackMode: e.target.value }) })}
              style={{width:'100%',padding:'8px 10px',border:'1px solid #ddd',borderRadius:8,fontSize:13}}>
              <option value="openrouter">Use OpenRouter (default)</option>
              <option value="error">Return error message</option>
              <option value="queue">Queue message for later</option>
            </select>
          </div>
          <div className="form-group">
            <label>Escalation keywords</label>
            <p style={{color:'#888',fontSize:11,margin:'0 0 4px'}}>Words that trigger immediate transfer to owner (one per line)</p>
            <textarea defaultValue={(c.escalationKeywords || []).join('\n')} rows={3}
              onBlur={e => authFetch("/api/aiassistant/config", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ escalationKeywords: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) }) })}
              placeholder="emergency&#10;urgent&#10;help" />
          </div>
        </div>
      )}

      {/* Tuning moved inside Access tab as sub-tab */}

      {subTab === 'Log' && <AccessLogSubTab />}

      {subTab === 'Security' && (
        <div className="settings-panel">
          <div className="form-group">
            <label>Owner Numbers (admin access by phone, one per line)</label>
            <textarea value={ownerNums} onChange={e => setOwnerNums(e.target.value)} rows={3} placeholder="+16507321013" />
          </div>
          <div className="form-group">
            <label>Transfer Allowlist (agent can transfer calls to these numbers)</label>
            <textarea value={allowlist} onChange={e => setAllowlist(e.target.value)} rows={3} placeholder="+16507321013 Nick&#10;+14155551234 Front desk" />
          </div>
          <button className="btn-save" onClick={() => handleSave()} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      )}

      {subTab === 'Numbers' && (
        <NumbersSubTab config={config} status={status} user={user} />
      )}

    </div>
  );
};


/* This file defines module-level constants and components used by AIAgentDashboard.
   All const declarations above are available as imports. */
export { EmailSubTab, VoiceCloner, CLONE_SCRIPT_OWNER, CLONE_SCRIPT_GUEST, POLLY_VOICES, EDGE_VOICES, VoiceSubTab, EnvKeysSubTab, TuningSubTab, AccessLogSubTab, OpenClawSubTab, NumbersSubTab, SETTINGS_SUBTABS, ACCESS_RESOURCES, DEFAULT_ACCESS, DEFAULT_GUEST_ACCESS, AccessWithTuneTab, SettingsTab };
